import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Zap, LogOut, Search, Download, Trash2, FileText, Phone, Mail,
  Users, TrendingUp, Loader2, RefreshCw, StickyNote, X, Eye, EyeOff,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["nuevo", "contactado", "estudiando", "ganado", "perdido"];
const STATUS_COLORS = {
  nuevo: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
  contactado: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  estudiando: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  ganado: "bg-green-400/15 text-green-300 border-green-400/30",
  perdido: "bg-red-400/15 text-red-300 border-red-400/30",
};
const PROFILES = { particular: "Particular", pyme: "Empresa", comunidad: "Comunidad" };

const getToken = () => localStorage.getItem("amql_token");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* ---------- Login ---------- */
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("amql_token", data.access_token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-4" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-xl bg-[#FFD600] flex items-center justify-center">
            <Zap className="w-6 h-6 text-black" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <p className="text-white font-bold text-lg leading-tight" style={{ fontFamily: "Fraunces, serif" }}>AlgoMásQueLuz</p>
            <p className="text-white/50 text-xs">Panel de administración</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 space-y-5">
          <div>
            <label className="block text-white/60 text-sm mb-1.5">Email</label>
            <input
              data-testid="admin-login-email"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFD600]/60 transition-colors"
              placeholder="estudios@algomasqueluz.com"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1.5">Contraseña</label>
            <div className="relative">
              <input
                data-testid="admin-login-password"
                type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 pr-11 text-white placeholder-white/30 focus:outline-none focus:border-[#FFD600]/60 transition-colors"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit" disabled={loading}
            className="w-full bg-[#FFD600] hover:bg-[#ffdf33] text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------- Stats ---------- */
function StatsBar({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: "Total leads", value: stats.total, icon: Users },
    { label: "Este mes", value: stats.this_month, icon: TrendingUp },
    { label: "Nuevos", value: stats.by_status?.nuevo ?? 0, icon: Zap },
    { label: "Ganados", value: stats.by_status?.ganado ?? 0, icon: TrendingUp },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- Notes modal ---------- */
function NotesModal({ lead, onClose, onSave }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await onSave(lead.id, notes);
    setSaving(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-white/15 rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold" style={{ fontFamily: "Fraunces, serif" }}>Notas — {lead.name}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <textarea
          data-testid="lead-notes-textarea"
          value={notes} onChange={(e) => setNotes(e.target.value)} rows={5}
          className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFD600]/60"
          placeholder="Escribe notas internas sobre este lead..."
        />
        <button
          data-testid="lead-notes-save"
          onClick={save} disabled={saving}
          className="mt-4 w-full bg-[#FFD600] hover:bg-[#ffdf33] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60"
        >
          {saving ? "Guardando..." : "Guardar notas"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Lead card ---------- */
function LeadCard({ lead, onStatus, onDelete, onNotes, onDownload }) {
  const status = lead.status || "nuevo";
  return (
    <div data-testid={`lead-card-${lead.id}`} className="bg-white/[0.04] border border-white/10 rounded-xl p-4 sm:p-5 hover:border-white/20 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-bold truncate">{lead.name}</p>
            {lead.profile && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">{PROFILES[lead.profile] || lead.profile}</span>
            )}
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[status]}`}>{status}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60">
            <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-[#FFD600]"><Mail className="w-3.5 h-3.5" />{lead.email}</a>
            {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-[#FFD600]"><Phone className="w-3.5 h-3.5" />{lead.phone}</a>}
          </div>
          {lead.message && <p className="mt-2 text-sm text-white/50 line-clamp-2">"{lead.message}"</p>}
          {lead.notes && (
            <p className="mt-2 text-xs text-[#FFD600]/80 flex items-start gap-1.5">
              <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0" />{lead.notes}
            </p>
          )}
          <p className="mt-2 text-xs text-white/35">{fmtDate(lead.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <select
            data-testid={`lead-status-select-${lead.id}`}
            value={status}
            onChange={(e) => onStatus(lead.id, e.target.value)}
            className="bg-black/40 border border-white/15 rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-[#FFD600]/60 cursor-pointer"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1.5">
            {lead.file?.stored_name && (
              <button
                data-testid={`lead-download-file-${lead.id}`}
                onClick={() => onDownload(lead.file)}
                title={`Descargar: ${lead.file.original_name}`}
                className="p-2 rounded-lg bg-[#FFD600]/10 text-[#FFD600] hover:bg-[#FFD600]/20 transition-colors"
              >
                <FileText className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onNotes(lead)}
              title="Notas"
              className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
            >
              <StickyNote className="w-4 h-4" />
            </button>
            <button
              data-testid={`lead-delete-${lead.id}`}
              onClick={() => onDelete(lead)}
              title="Eliminar"
              className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [profileFilter, setProfileFilter] = useState("");
  const [notesLead, setNotesLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (profileFilter) params.profile = profileFilter;
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setLeads(leadsRes.data.items);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Error cargando leads");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, profileFilter, onLogout]);

  useEffect(() => {
    const t = setTimeout(load, q ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Estado actualizado a "${status}"`);
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }).then((r) => setStats(r.data));
    } catch {
      toast.error("Error actualizando estado");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { notes }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes } : l)));
      toast.success("Notas guardadas");
    } catch {
      toast.error("Error guardando notas");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`¿Eliminar el lead de ${lead.name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, { headers: authHeaders() });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success("Lead eliminado");
    } catch {
      toast.error("Error eliminando lead");
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}/admin/uploads/${file.stored_name}`, {
        headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name || file.stored_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error descargando el archivo");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads.csv`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error exportando CSV");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#FFD600] flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "Fraunces, serif" }}>AlgoMásQueLuz</p>
              <p className="text-white/40 text-[11px]">Gestión de leads</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-export-csv"
              onClick={downloadCsv}
              className="hidden sm:flex items-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white/80 px-3 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
            <button onClick={load} title="Recargar" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="flex items-center gap-1.5 text-sm bg-white/5 hover:bg-red-500/15 text-white/70 hover:text-red-300 px-3 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-dashboard">
        <StatsBar stats={stats} />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, email o teléfono..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#FFD600]/60 transition-colors"
            />
          </div>
          <select
            data-testid="admin-filter-status"
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-white/80 focus:outline-none focus:border-[#FFD600]/60 cursor-pointer"
          >
            <option value="" className="bg-[#1a1a1a]">Todos los estados</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>)}
          </select>
          <select
            data-testid="admin-filter-profile"
            value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)}
            className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-white/80 focus:outline-none focus:border-[#FFD600]/60 cursor-pointer"
          >
            <option value="" className="bg-[#1a1a1a]">Todos los perfiles</option>
            <option value="particular" className="bg-[#1a1a1a]">Particular</option>
            <option value="pyme" className="bg-[#1a1a1a]">Empresa</option>
            <option value="comunidad" className="bg-[#1a1a1a]">Comunidad</option>
          </select>
        </div>

        {/* Leads list */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20 text-white/40" data-testid="admin-empty-state">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No hay leads con estos filtros</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-white/40 text-sm">{leads.length} lead{leads.length !== 1 ? "s" : ""}</p>
            {leads.map((lead) => (
              <LeadCard
                key={lead.id} lead={lead}
                onStatus={updateStatus} onDelete={deleteLead}
                onNotes={setNotesLead} onDownload={downloadFile}
              />
            ))}
          </div>
        )}
      </main>

      {notesLead && <NotesModal lead={notesLead} onClose={() => setNotesLead(null)} onSave={saveNotes} />}
    </div>
  );
}

/* ---------- Root ---------- */
export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem("amql_token"); setAuthed(false); });
  }, []);

  const logout = () => {
    localStorage.removeItem("amql_token");
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#FFD600]" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors theme="dark" />
      {authed ? <Dashboard onLogout={logout} /> : <LoginScreen onLogin={() => setAuthed(true)} />}
    </>
  );
}
